import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertOctagon, Banknote, Bell, BookMarked, Briefcase, Building2, CheckSquare, ChevronRight, ClipboardList, FileCheck2, FileSearch, FileSignature, FileText, FlaskConical, FolderKanban,
  BadgeEuro, CalendarDays, FileBarChart, Gauge, GitCompare, HandCoins, HardHat, History, Inbox, LayoutDashboard, LibraryBig, ListChecks, LogOut, Mail, Menu, Minus, PackageCheck, PenLine, Play, PlugZap, ReceiptText, RotateCcw, Scale, ScrollText, Search, ShieldCheck, ShoppingCart, Truck, User, Wallet, X,
} from "lucide-react";
import { BrandMark } from "./common";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AREAS, fmtDateTime } from "../lib/labels";
import { userInitials, userName, useUsers } from "../lib/hooks";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { toast } from "sonner";
import CommandPalette from "./CommandPalette";

const NAV = [
  { to: "/", label: "Inicio", icon: LayoutDashboard, exact: true },
  { to: "/control-corporativo", label: "Control corporativo", icon: Gauge },
  { to: "/mi-trabajo", label: "Mi trabajo", icon: CheckSquare, badgeKey: "tasks" },
  { to: "/expedientes", label: "Expedientes", icon: FolderKanban },
  { to: "/aprobaciones", label: "Aprobaciones", icon: ShieldCheck, badgeKey: "approvals" },
  { to: "/evidencias", label: "Evidencias", icon: FileCheck2, badgeKey: "evidence" },
  { to: "/documentos", label: "Documentos", icon: FileText },
  { header: "Documentos y correo" },
  { to: "/documentos/corp", label: "Gestión documental", icon: LibraryBig },
  { to: "/correo", label: "Correo — Smart Inbox", icon: Mail },
  { to: "/polizas", label: "Pólizas", icon: ShieldCheck },
  { to: "/vacaciones", label: "Vacaciones", icon: CalendarDays },
  { to: "/comunicaciones", label: "Comunicaciones", icon: Inbox, badgeKey: "comms" },
  { to: "/actividad", label: "Actividad", icon: History },
  { to: "/buscar", label: "Buscar", icon: Search },
  { header: "Contratos y Legal" },
  { to: "/legal", label: "Cuadro de mando", icon: LayoutDashboard, exact: true },
  { to: "/legal/contratos", label: "Contratos", icon: Scale, badgeKey: "contracts" },
  { to: "/legal/obligaciones", label: "Obligaciones", icon: ListChecks, badgeKey: "obligations" },
  { to: "/legal/revisiones", label: "Revisiones", icon: FileSearch },
  { to: "/legal/comparaciones", label: "Comparaciones", icon: GitCompare },
  { to: "/legal/nda-loi", label: "NDA y LOI", icon: FileSignature },
  { to: "/legal/firmas", label: "Firmas", icon: PenLine, badgeKey: "signatures" },
  { to: "/legal/modelos", label: "Modelos y precedentes", icon: LibraryBig },
  { to: "/legal/clausulas", label: "Biblioteca de cláusulas", icon: BookMarked },
  { header: "Finanzas y Compras" },
  { to: "/finanzas", label: "Cuadro de mando", icon: LayoutDashboard, exact: true },
  { to: "/finanzas/solicitudes", label: "Solicitudes de compra", icon: ShoppingCart },
  { to: "/finanzas/ofertas", label: "Ofertas de proveedores", icon: ClipboardList },
  { to: "/finanzas/comparaciones", label: "Comparaciones", icon: GitCompare },
  { to: "/finanzas/ordenes", label: "Órdenes de compra", icon: Truck },
  { to: "/finanzas/recepciones", label: "Recepciones", icon: PackageCheck, badgeKey: "fin_receipts" },
  { to: "/finanzas/facturas", label: "Facturas recibidas", icon: ReceiptText, badgeKey: "fin_invoices" },
  { to: "/finanzas/facturacion", label: "Facturación a clientes", icon: HandCoins, badgeKey: "fin_ar" },
  { to: "/finanzas/excepciones", label: "Excepciones", icon: AlertOctagon, badgeKey: "fin_exceptions" },
  { to: "/finanzas/remesas", label: "Remesas y pagos", icon: Banknote, badgeKey: "fin_batches" },
  { to: "/finanzas/proveedores", label: "Proveedores", icon: Building2, badgeKey: "fin_bank" },
  { to: "/finanzas/presupuesto", label: "Control presupuestario", icon: Wallet },
  { header: "Proyectos" },
  { to: "/proyectos", label: "Cuadro de mando", icon: LayoutDashboard, exact: true, badgeKey: "proj" },
  { to: "/proyectos/portafolio", label: "Portafolio", icon: Briefcase },
  { to: "/proyectos/lista", label: "Proyectos", icon: FolderKanban },
  { to: "/proyectos/planificacion", label: "Planificación", icon: GitCompare },
  { to: "/proyectos/economia", label: "Economía", icon: BadgeEuro },
  { to: "/proyectos/recursos", label: "Recursos", icon: User },
  { to: "/proyectos/riesgos", label: "Riesgos", icon: AlertOctagon },
  { to: "/proyectos/calidad", label: "Calidad", icon: ClipboardList },
  { to: "/proyectos/permisos", label: "Permisos", icon: ScrollText },
  { to: "/proyectos/acometidas", label: "Acometidas", icon: PlugZap },
  { to: "/proyectos/seguridad", label: "Seguridad y Salud", icon: HardHat },
  { to: "/proyectos/reporting", label: "Reporting", icon: FileBarChart },
  { to: "/reuniones", label: "Reuniones", icon: CalendarDays },
  { header: "Demo" },
  { to: "/demo-center", label: "Demo Center", icon: Play },
  { to: "/escenarios", label: "Escenarios demo", icon: FlaskConical },
];

const CRUMBS = {
  "": "Inicio", "mi-trabajo": "Mi trabajo", expedientes: "Expedientes", aprobaciones: "Aprobaciones",
  evidencias: "Evidencias", documentos: "Documentos", comunicaciones: "Comunicaciones",
  actividad: "Actividad", buscar: "Buscar", escenarios: "Escenarios de demostración",
  legal: "Contratos y Legal", contratos: "Contratos", obligaciones: "Obligaciones", revisiones: "Revisiones",
  comparaciones: "Comparaciones", "nda-loi": "NDA y LOI", firmas: "Firmas", modelos: "Modelos y precedentes",
  clausulas: "Biblioteca de cláusulas",
  finanzas: "Finanzas y Compras", solicitudes: "Solicitudes de compra", ofertas: "Ofertas de proveedores",
  ordenes: "Órdenes de compra", recepciones: "Recepciones y conformidades", facturas: "Facturas de proveedores",
  facturacion: "Facturación a clientes", excepciones: "Excepciones", remesas: "Remesas y pagos",
  proveedores: "Proveedores", presupuesto: "Control presupuestario",
  proyectos: "Proyectos", portafolio: "Portafolio", lista: "Proyectos",
  riesgos: "Riesgos", calidad: "Calidad", permisos: "Permisos", acometidas: "Acometidas",
  seguridad: "Seguridad y Salud", reporting: "Reporting", reuniones: "Reuniones",  planificacion: "Planificación", recursos: "Recursos", economia: "Economía",
  "cuadro-de-mando": "Cuadro de mando", seguimiento: "Seguimiento", acta: "Acta", validacion: "Validación IA", portal: "Portal de participante",
};

function NavItems({ badges, onNavigate, external }) {
  // usuarios externos (contratistas): solo lo operativo propio; el resto de verticales no les aplica
  const items = external ? NAV.filter((i) => ["/", "/mi-trabajo", "/buscar"].includes(i.to)) : NAV;
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map((item, idx) => {
        if (item.header) {
          return <p key={`h-${idx}`} className="mb-1 mt-4 px-3 text-[10px] font-semibold uppercase tracking-widest text-[#8A9CC9]">{item.header}</p>;
        }
        const badge = badges?.[item.badgeKey];
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.exact}
            onClick={onNavigate}
            data-testid={`nav-item-${item.label.toLowerCase().replace(/ /g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-sm px-3 py-1.5 text-sm font-medium transition-colors ${
                isActive ? "ts-sidebar-item-active" : "ts-sidebar-item"
              }`
            }
          >
            <item.icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 truncate">{item.label}</span>
            {badge > 0 && (
              <span className="rounded-sm bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white" data-testid={`nav-badge-${item.badgeKey}`}>{badge}</span>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function GuidedPanel() {
  const [guide, setGuide] = useState(() => JSON.parse(localStorage.getItem("ts_demo_guide") || "null"));
  const [min, setMin] = useState(false);
  const navigate = useNavigate();
  const { data } = useQuery({ queryKey: ["demo-mode-esc"], queryFn: async () => (await api.get("/demo-mode/escenarios")).data, enabled: !!guide });
  useEffect(() => {
    const h = () => setGuide(JSON.parse(localStorage.getItem("ts_demo_guide") || "null"));
    window.addEventListener("ts-demo-guide", h);
    window.addEventListener("storage", h);
    return () => { window.removeEventListener("ts-demo-guide", h); window.removeEventListener("storage", h); };
  }, []);
  if (!guide?.open) return null;
  const story = data?.scenarios?.find((s) => s.id === guide.story);
  const steps = story?.steps || [];
  if (!steps.length) return null;
  const idx = Math.min(guide.step || 0, steps.length - 1);
  const step = steps[idx];
  const goStep = (i) => {
    const g = { ...guide, step: i };
    localStorage.setItem("ts_demo_guide", JSON.stringify(g));
    setGuide(g);
    if (steps[i]?.route) navigate(steps[i].route);
  };
  const close = () => { localStorage.removeItem("ts_demo_guide"); setGuide(null); };
  if (min) {
    return (
      <button onClick={() => setMin(false)} data-testid="demo-guide-reopen"
        className="fixed bottom-4 right-4 z-50 rounded-sm bg-[#0F172A] px-3 py-2 text-xs font-semibold text-white shadow-md">
        Paso {idx + 1}/{steps.length} — {story.name}
      </button>
    );
  }
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80 rounded-sm border border-slate-300 bg-white p-3 shadow-md" data-testid="demo-guide-panel">
      <div className="flex items-center justify-between">
        <p className="ts-caption">Demo guiada · {story.name}</p>
        <div className="flex gap-1">
          <button onClick={() => setMin(true)} className="text-slate-400 hover:text-slate-700" data-testid="demo-guide-min"><Minus className="h-3.5 w-3.5" /></button>
          <button onClick={close} className="text-slate-400 hover:text-slate-700" data-testid="demo-guide-close"><X className="h-3.5 w-3.5" /></button>
        </div>
      </div>
      <p className="mt-1 text-xs font-bold text-slate-500" data-testid="demo-guide-count">PASO {idx + 1} DE {steps.length}</p>
      <p className="mt-1 text-sm text-slate-700" data-testid="demo-guide-step">{step.text}</p>
      <div className="mt-2 flex gap-1.5">
        {step.route && <Button size="sm" className="h-7 text-xs" onClick={() => navigate(step.route)} data-testid="demo-guide-goto">Ir al paso</Button>}
        {idx < steps.length - 1
          ? <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => goStep(idx + 1)} data-testid="demo-guide-next">Siguiente paso <ChevronRight className="ml-1 h-3 w-3" /></Button>
          : <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => { close(); navigate("/demo-center"); }} data-testid="demo-guide-finish">Finalizar historia</Button>}
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  const { user, logout, area, setArea } = useAuth();
  const { map } = useUsers();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const { data: dash } = useQuery({
    queryKey: ["dashboard-badges", area],
    queryFn: async () => (await api.get("/dashboard", { params: { area } })).data,
    staleTime: 30_000,
  });
  const { data: notifs } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    staleTime: 20_000,
  });

  const badges = useMemo(() => ({
    tasks: (dash?.my_work?.vencido || 0) + (dash?.attention?.evidence_review || 0),
    approvals: dash?.attention?.approvals_pending || 0,
    evidence: dash?.attention?.evidence_review || 0,
    comms: dash?.attention?.comms_urgent || 0,
    contracts: (dash?.legal?.mi_validacion || 0) + (dash?.legal?.riesgos_criticos || 0),
    obligations: dash?.legal?.obligaciones_vencidas || 0,
    signatures: dash?.legal?.firmas_en_curso || 0,
    fin_invoices: dash?.finance?.facturas_pendientes || 0,
    fin_exceptions: dash?.finance?.excepciones_criticas || 0,
    fin_ar: dash?.finance?.cobros_vencidos || 0,
    fin_batches: dash?.finance?.remesas_en_curso || 0,
    fin_receipts: dash?.finance?.conformidades_pendientes || 0,
    fin_bank: dash?.finance?.cambios_bancarios || 0,
    proj: (dash?.projects?.bloqueados || 0) + (dash?.projects?.gates_en_revision || 0),
  }), [dash]);

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const segments = location.pathname.split("/").filter(Boolean);

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const restoreDemo = async () => {
    setRestoring(true);
    try {
      await api.post("/demo/restore");
      toast.success("Datos demo restaurados", { description: "TECH_STONE_FOUNDATION_DEMO_V1" });
      setTimeout(() => window.location.reload(), 600);
    } catch (e) {
      toast.error("No se pudo restaurar", { description: e?.response?.data?.detail || "Se requiere rol DEMO ADMIN" });
    } finally {
      setRestoring(false);
    }
  };

  const sidebarContent = (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center border-b border-[#122C7A] px-4">
        <BrandMark />
      </div>
      <div className="flex-1 overflow-y-auto py-4">
        <NavItems badges={badges} onNavigate={() => setMobileOpen(false)} external={(user?.roles || []).length === 1 && (user?.roles || []).includes("VIEWER")} />
      </div>
      <div className="border-t border-[#122C7A] p-3 text-[10px] leading-relaxed text-[#8A9CC9]">
        Seed: TECH_STONE_FOUNDATION_DEMO_V1
        <br />Entorno de demostración — datos ficticios
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen">
      <aside className="ts-sidebar fixed inset-y-0 left-0 z-40 hidden w-64 border-r border-[#122C7A] lg:block" data-testid="sidebar">
        {sidebarContent}
      </aside>

      <div className="flex min-h-screen w-full min-w-0 max-w-full flex-1 flex-col overflow-x-hidden lg:pl-64">
        <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-slate-200 bg-white/95 px-3 backdrop-blur-md sm:px-5" data-testid="topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-button" aria-label="Abrir menú">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 border-[#122C7A] ts-sidebar p-0">{sidebarContent}</SheetContent>
          </Sheet>

          <nav className="hidden items-center gap-1 text-sm text-slate-500 md:flex" aria-label="breadcrumb" data-testid="breadcrumbs">
            <Link to="/" className="hover:text-slate-900">Inicio</Link>
            {segments.map((seg, i) => (
              <React.Fragment key={i}>
                <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
                {i === segments.length - 1 ? (
                  <span className="font-medium text-slate-900">{CRUMBS[seg] || location.state?.crumb || "Detalle"}</span>
                ) : (
                  <Link to={`/${segments.slice(0, i + 1).join("/")}`} className="hover:text-slate-900">{CRUMBS[seg] || seg}</Link>
                )}
              </React.Fragment>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="h-8 w-[150px] text-xs sm:w-[190px]" data-testid="area-filter-select" aria-label="Selector de área">
                <SelectValue placeholder="Área" />
              </SelectTrigger>
              <SelectContent>
                {AREAS.map((a) => <SelectItem key={a} value={a} data-testid={`area-option-${a}`}>{a}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" className="hidden h-8 gap-2 text-xs text-slate-500 sm:flex" onClick={() => setPaletteOpen(true)} data-testid="command-palette-trigger">
              <Search className="h-3.5 w-3.5" /> Buscar…
              <kbd className="rounded border border-slate-200 bg-slate-50 px-1 font-mono text-[10px]">Ctrl K</kbd>
            </Button>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-8 w-8" data-testid="notifications-trigger" aria-label="Notificaciones">
                  <Bell className="h-4 w-4" />
                  {notifs?.unread > 0 && (
                    <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white" data-testid="notifications-badge">
                      {notifs.unread}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 p-0" data-testid="notifications-panel">
                <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                  <p className="text-sm font-semibold">Notificaciones</p>
                  <button onClick={markAllRead} className="text-xs font-medium text-blue-700 hover:underline" data-testid="notifications-read-all">Marcar todas leídas</button>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {(notifs?.items || []).length === 0 && <p className="p-6 text-center text-sm text-slate-500">Sin notificaciones</p>}
                  {(notifs?.items || []).slice(0, 25).map((n) => (
                    <Link
                      key={n.id}
                      to={n.link || "/"}
                      data-testid={`notification-${n.id}`}
                      className={`block border-b border-slate-50 px-4 py-3 transition-colors hover:bg-slate-50 ${!n.read ? "bg-blue-50/40" : ""}`}
                    >
                      <div className="flex items-start gap-2">
                        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.severity === "critical" ? "bg-red-500" : n.severity === "warning" ? "bg-amber-500" : "bg-sky-500"}`} />
                        <div className="min-w-0">
                          <p className="text-sm leading-snug text-slate-800">{n.reason}</p>
                          <p className="mt-0.5 text-xs text-slate-400">{n.origin} · {fmtDateTime(n.date)}{n.recommended_action ? ` · ${n.recommended_action}` : ""}</p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2 rounded-sm px-1.5 py-1 hover:bg-slate-100" data-testid="profile-menu-trigger">
                  <span className="flex h-7 w-7 items-center justify-center bg-[#122C7A] text-xs font-semibold text-white" style={{ borderRadius: 0 }}>{userInitials(user?.name)}</span>
                  <span className="hidden text-left sm:block">
                    <span className="block text-xs font-medium leading-tight text-slate-900">{user?.name}</span>
                    <span className="block text-[10px] leading-tight text-slate-500">{(user?.roles || []).join(" · ")}</span>
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="profile-menu">
                <DropdownMenuLabel>
                  <p className="text-sm font-semibold">{user?.name}</p>
                  <p className="text-xs font-normal text-slate-500">{user?.title} — {user?.area}</p>
                  <p className="mt-1 text-xs font-normal text-slate-400">{user?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="text-xs">
                  <User className="mr-2 h-3.5 w-3.5" /> Roles: {(user?.roles || []).join(", ")}
                </DropdownMenuItem>
                {user?.roles?.includes("DEMO_ADMIN") && (
                  <DropdownMenuItem onClick={restoreDemo} disabled={restoring} data-testid="restore-demo-button">
                    <RotateCcw className="mr-2 h-3.5 w-3.5" /> {restoring ? "Restaurando…" : "Restaurar datos demo"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} data-testid="logout-button">
                  <LogOut className="mr-2 h-3.5 w-3.5" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="w-full min-w-0 max-w-full flex-1 overflow-x-hidden px-4 py-6 sm:px-6 lg:px-8" data-testid="main-content">{children}</main>
      </div>

      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} />
      <GuidedPanel />
    </div>
  );
}
